import { Dispatch, FC, SetStateAction } from 'react';

import { useDialog } from 'hooks/useDialog';

import { RaaDialog, RaaSelect } from 'raa-react';

import TagList from 'components/tagList/TagList';
import { EnskiltFormValues } from 'components/utkast/utkastRegistration/UtkastFormSchema';

import { Egenskap } from 'models/apiModels/Egenskap';
import { EgenskapsTyp } from 'models/apiModels/EgenskapsTyp';
import { EgenskapsVardeTyp } from 'models/apiModels/egenskapsVardeTyp';

import { ErrorMessage, useFormikContext } from 'formik';

interface egenskaperWithTaglistProps {
  egenskaper: Array<EgenskapsTyp>;
  selectedEgenskaper: Array<Egenskap>;
  setSelectedEgenskaper: Dispatch<SetStateAction<Egenskap[]>>;
}

const EgenskaperWithTagList: FC<egenskaperWithTaglistProps> = ({
  egenskaper,
  selectedEgenskaper,
  setSelectedEgenskaper
}) => {
  const { setFieldValue, errors } = useFormikContext<EnskiltFormValues>();
  const { setDialog, unsetDialog } = useDialog();

  const isInaktiv = (egenskap: Egenskap, egenskapsTyp: EgenskapsTyp) => {
    return (
      !egenskapsTyp.aktiv ||
      egenskaper.find((x) =>
        x.egenskapsvarden.find((y) => y.egenskapsvarde_id === egenskap.egenskapsvarde_id && !y.aktiv)
      )
    );
  };

  const removeEgenskap = (toRemove: Egenskap) => {
    setFieldValue(
      'egenskaper',
      selectedEgenskaper
        .filter((egenskap: Egenskap) => egenskap.egenskapsvarde_id !== toRemove.egenskapsvarde_id)
        .map((egenskap: Egenskap) => egenskap.egenskapsvarde_id)
    );
    setSelectedEgenskaper((current: Egenskap[]) =>
      current.filter((egenskap: Egenskap) => egenskap.egenskapsvarde_id !== toRemove.egenskapsvarde_id)
    );
  };

  const onClickRemoveEgenskapTag = (removedItem: Egenskap, egenskapsTyp: EgenskapsTyp) => {
    if (isInaktiv(removedItem, egenskapsTyp)) {
      setDialog(RaaDialog, {
        header: 'Ta bort utfasat värde?',
        ariaDescribedBy: 'dialog-description',
        children: (
          <p id="dialog-description" className="tw-my-4">
            Du kommer inte att kunna välja den här egenskapen igen.
          </p>
        ),
        cancel: () => unsetDialog(),
        cancelButtonText: 'Avbryt',
        proceedButtonText: 'Ta bort',
        proceed: () => {
          removeEgenskap(removedItem);
          unsetDialog();
        }
      });
    } else {
      removeEgenskap(removedItem);
    }
  };

  return (
    <>
      {egenskaper
        .filter(
          (egenskapsTyp) =>
            egenskapsTyp.aktiv ||
            selectedEgenskaper.some((egenskap) => egenskap.egenskapstyp_id === egenskapsTyp.egenskapstyp)
        )
        .map((egenskapsTyp) => (
          <div key={egenskapsTyp.namn}>
            <label className="raa-label tw-mt-4" htmlFor={egenskapsTyp.namn}>
              {egenskapsTyp.namn}
            </label>
            <RaaSelect
              clearInputOnSelection={true}
              inputElementId={egenskapsTyp.namn}
              domain={
                egenskapsTyp.aktiv
                  ? egenskapsTyp.egenskapsvarden.filter(
                      (egenskapsVardeTyp: EgenskapsVardeTyp) =>
                        !selectedEgenskaper.some(
                          (selected: Egenskap) => selected.egenskapsvarde_id === egenskapsVardeTyp.egenskapsvarde_id
                        ) && egenskapsVardeTyp.aktiv
                    )
                  : []
              }
              displayAttr="namn"
              onSelect={(a: EgenskapsVardeTyp) => {
                setFieldValue('egenskaper', [
                  ...selectedEgenskaper.map((e) => e.egenskapsvarde_id),
                  a.egenskapsvarde_id
                ]);

                setSelectedEgenskaper((current) => [
                  ...current,
                  {
                    egenskapsvarde_id: a.egenskapsvarde_id,
                    egenskapstyp_id: egenskapsTyp.egenskapstyp,
                    egenskapsvarde_namn: a.namn,
                    egenskapstyp_namn: egenskapsTyp.namn
                  }
                ]);
              }}
              renderOption={(egenskapsVardeTyp: EgenskapsVardeTyp) => (
                <span className="tw-capitalize">{egenskapsVardeTyp.namn}</span>
              )}
            />

            <div className="tw-mt-2">
              <TagList
                list={selectedEgenskaper.filter(
                  (egenskap: Egenskap) => egenskap.egenskapstyp_id === egenskapsTyp.egenskapstyp
                )}
                leftColumn={(egenskap: Egenskap) => (
                  <span className="tw-capitalize">{egenskap.egenskapsvarde_namn}</span>
                )}
                rightColumn={(egenskap: Egenskap) => (isInaktiv(egenskap, egenskapsTyp) ? ' (utfasad egenskap)' : '')}
                onRemoveItem={(toRemove: Egenskap) => {
                  onClickRemoveEgenskapTag(toRemove, egenskapsTyp);
                }}
              />
              {!!errors.egenskaper &&
                egenskapsTyp.egenskapsvarden.some((egenskapsVardeTyp) => !egenskapsVardeTyp.aktiv) && (
                  <ErrorMessage name={'egenskaper'} component="p" className="tw-mt-2 tw-text-raa-error-red" />
                )}
            </div>
          </div>
        ))}
    </>
  );
};

export default EgenskaperWithTagList;
